/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import React, { useContext, useMemo } from 'react';
import {
  Question,
  FlowBlockQuestion as QuestionBlockModel,
} from '@staffino/model';
import {
  OpenQuestionBlock,
  ScaleQuestionBlock,
  ChoiceQuestionBlock,
} from '@staffino/flow-controls';
import { useFormContext } from 'react-hook-form';
import useObjects from '../use-objects-hook';
import FlowContext from '../flow-context';
import useTemplateRenderer from '../use-template-renderer';

type Props = {
  block: QuestionBlockModel;
};

export default function QuestionBlock({ block }: Props) {
  const [question] = useObjects('question', { id: block.questionId });
  const choices = useObjects('choice', { questionId: block.questionId });
  const [
    placeholder,
    description,
    title,
    label,
    commentTitle,
    commentLabel,
    lowerBoundLabel,
    upperBoundLabel,
  ] = useTemplateRenderer(
    question?.placeholder,
    question?.description,
    question?.title,
    question?.label,
    question?.commentTitle,
    question?.commentLabel,
    question?.lowerBoundLabel,
    question?.upperBoundLabel,
  );
  // we need to keep choiceTitles stable between re-renders,
  // therefore they have its own useTemplateRenderer call
  const choiceTitles = useTemplateRenderer(
    ...choices.map((choice) => choice.title),
  );
  const renderedBlock = { ...block };
  const renderedQuestion: Question = {
    ...question,
    // id: question?.id,
    // organizationId: question?.organizationId,
    // localeId: question?.organizationId,
    description,
    title,
    label,
    commentTitle,
    commentLabel,
    lowerBoundLabel,
    upperBoundLabel,
    placeholder,
  };
  const renderedChoices = useMemo(
    () =>
      choices.map((choice, index) => ({
        ...choice,
        title: choiceTitles[index],
      })),
    [choices, choiceTitles],
  );
  const { stateManager, handleSubmit } = useContext(FlowContext);
  const context = useFormContext();

  // go to the next page when user selected value and autoSubmit is true
  const handleSelect = (score) => {
    if (block.autoSubmit && !Number.isNaN(Number.parseInt(score, 10))) {
      stateManager.updateFormData({ [block.id]: { score } });
      handleSubmit?.(context?.getValues({ nest: true }));
    }
  };

  if (question?.type === 'Text') {
    return (
      <OpenQuestionBlock block={renderedBlock} question={renderedQuestion} />
    );
  }
  if (question?.type === 'Scale') {
    return (
      <ScaleQuestionBlock
        block={renderedBlock}
        question={renderedQuestion}
        choices={renderedChoices}
        onSelect={handleSelect}
      />
    );
  }
  if (question?.type === 'Choice') {
    return (
      <ChoiceQuestionBlock
        block={renderedBlock}
        question={renderedQuestion}
        choices={renderedChoices}
      />
    );
  }
  return null;
}
